/**
 * TEC/MAG projectile accuracy — the single runtime authority for turning a
 * live actor's precision stat (TEC for a WEAPON/BODY source, MAG for a
 * MAGIC source — the same split `fireBasicAttackProjectile` already uses)
 * into an activation-frozen effective `projectile` Delivery, for SKILL
 * projectiles only. Basic-attack projectiles keep their own pre-existing
 * absolute speed curve untouched by this module's transform — see the
 * exemption note on `effectiveProjectileDelivery`.
 *
 * `effectiveProjectileDelivery` is captured exactly once by `executeSkill`
 * immediately after the runtime skill is confirmed — the same ownership
 * rule `captureSkillMasterySnapshot` (`./mastery.ts`) already establishes:
 * captured once at cast acceptance, never re-queried mid-execution, and
 * never written back to `OwnedSkill.definition` / `canonicalDefinition` /
 * `runtimePlan`. A non-`projectile` delivery passes through unchanged
 * (the identity `delivery` object itself, not a clone).
 *
 * Deliberately separate from skill *value*: nothing exported here is read
 * by `src/eval/**`, `src/generation/**`, `src/burst/**`, or
 * `src/skill-engine/**`. `critChance` is the existing precedent for this
 * split — TEC-driven, never touched by appraisal/generation/burst, only
 * consumed inside `src/sim/`.
 */

import type { Delivery, SourceRequirement } from '../types.js';
import type { PrimaryStats } from './primary-stats.js';

/**
 * The reference character this system normalizes every other build
 * against — the FLOOR of the curve, not a midpoint. `3 * 1 = 3`: the
 * canonical 3:1:1 build's raw main-stat points at level 1.
 *
 * Anchoring at `CANONICAL_GENERATION_EVALUATION_ANCHOR.level` (60, raw 180)
 * instead would leave most of the levelling range BELOW the anchor and
 * penalize it, and any meaningful top-end gain would cost a larger
 * bottom-end nerf. Anchoring at L1 makes every non-negative stat factor
 * >= 1.0: no build is ever penalized below authored values, and the full
 * curve is a pure bonus that grows with investment.
 *
 * PROVISIONAL: 3 is a fixed floor point (L1's raw main-stat under the
 * ratified 3:1:1 shape), not a claim that L1 is the correct precision
 * floor in some deeper sense. A future calibration pass may still replace
 * this anchor.
 */
export const PROJECTILE_PRECISION_ANCHOR_RAW_STAT_POINTS = 3 * 1; // PROVISIONAL — the L1 floor, not CANONICAL_GENERATION_EVALUATION_ANCHOR.level

/**
 * Interpolation anchors: the canonical build's raw main-stat points at L1
 * and L100 (`3 * level`, matching
 * `PRIMARY_V0_CANONICAL_3_1_1_SHAPE_BY_LEVEL`) — shared by every
 * projectile-precision curve (speed, radius) below.
 */
const CURVE_REFERENCE_POINTS_AT_L1 = 3 * 1;
const CURVE_REFERENCE_POINTS_AT_L100 = 3 * 100;

/** Shared interpolation core for every projectile-precision curve: a
 * straight line in raw stat points through the two anchors. `statPoints`
 * is the firing actor's own raw primary-stat value, never a canonical/
 * reference value; only the two anchor multipliers move per curve/
 * source-class. The line is not capped here: above the L100 anchor it
 * keeps rising at the same slope. */
function linearByPoints(statPoints: number, multiplierAtL1: number, multiplierAtL100: number): number {
  const slope = (multiplierAtL100 - multiplierAtL1) / (CURVE_REFERENCE_POINTS_AT_L100 - CURVE_REFERENCE_POINTS_AT_L1);
  return multiplierAtL1 + slope * (Math.max(0, statPoints) - CURVE_REFERENCE_POINTS_AT_L1);
}

/**
 * Projectile speed multiplier curve — an ABSOLUTE multiplier over a fixed
 * reference speed. Formerly private to `authoritative-world.ts`; still
 * owned there for basic attacks (`PROJECTILE_SPEED_WEAPON/MAGIC_MULTIPLIER_
 * AT_L1/L100`, 10x-30x / 7x-20x), moved here only so that one module owns
 * the curve shape. `effectiveProjectileDelivery` below does NOT use this
 * function or those multipliers — see `PROJECTILE_SPEED_FACTOR_AT_L1/L100`.
 */
export function projectileSpeedMultiplier(statPoints: number, multiplierAtL1: number, multiplierAtL100: number): number {
  return linearByPoints(statPoints, multiplierAtL1, multiplierAtL100);
}

/** Bow (TEC-driven, `primary-stat-v0.ts`'s "projectile speed/control"
 * attribution note): L1 archer's arrow at 10x the reference speed, L100
 * archer's at 30x. Basic-attack-only — `fireBasicAttackProjectile` is the
 * sole caller (`authoritative-world.ts`); the general `executeSkill` seam
 * deliberately excludes basic attacks (see the exemption note on
 * `effectiveProjectileDelivery`). */
export const PROJECTILE_SPEED_WEAPON_MULTIPLIER_AT_L1 = 10;
export const PROJECTILE_SPEED_WEAPON_MULTIPLIER_AT_L100 = 30;
/** Intrinsic MAGIC (MAG-driven, "magical projectile potency/control"):
 * L1 caster's bolt at 7x, L100 caster's at 20x. Basic-attack-only, same
 * caveat as the Weapon pair above. */
export const PROJECTILE_SPEED_MAGIC_MULTIPLIER_AT_L1 = 7;
export const PROJECTILE_SPEED_MAGIC_MULTIPLIER_AT_L100 = 20;

/**
 * Skill-projectile precision factor curve — a RELATIVE multiplier
 * (normalized against the L1-floor `PROJECTILE_PRECISION_ANCHOR_RAW_
 * STAT_POINTS`, never below 1.0 for any non-negative stat). Two prior
 * designs were tried and abandoned here (owner corrections, 2026-09-17):
 *
 * 1. Reusing the basic-attack absolute 10x-30x/7x-20x multipliers as the
 *    relative factor put a Lv1-Lv59 character (raw main-stat < 180) at
 *    LESS than authored — a nerf on most of the levelling range.
 * 2. A compressed curve anchored at Lv60 (raw 180, factor(L1)=0.8,
 *    factor(L100)=1.0) fixed the direction but not the shape: with most
 *    of the levelling range below the anchor, any meaningful Lv100 payoff
 *    still cost a larger Lv1 penalty, and the bottom of the curve cannot
 *    absorb more.
 *
 * Anchoring at L1 (this version) removes the trade-off entirely: every
 * build is at or above authored values, and the curve is a pure bonus.
 *
 * Speed and radius intentionally use DIFFERENT anchors (1.0->1.5 vs.
 * 1.0->2.0), unlike the abandoned Lv60 version where they coincidentally
 * matched. Speed is a structurally weak accuracy lever: `aimSigma`'s
 * `AIM_SIGMA_BASE + 0.06*distanceM + 1.2*(distanceM/speedMps)` floors
 * sigma on its base+distance terms alone (at 12m, sigma >= ~1.07 even at
 * infinite speed, capping realized hit rate around 48.7% — a 1.5x speed
 * multiplier buys only a few points of that). Radius has no such ceiling:
 * doubling the collision radius doubles the effective hit window directly.
 * Radius therefore carries most of the intended accuracy gain; speed stays
 * the minor axis. This is exactly why the two curves are separately-named
 * constant pairs — splitting them was anticipated before it was needed.
 *
 * NOT AN AUTHORITATIVE BALANCE DECISION (`PROVISIONAL_UNCALIBRATED`,
 * matching the vocabulary `PlaceholderNonDamageComponentEvidence` —
 * `src/types.ts` — uses for the same situation): these anchors are a
 * bootstrap, not a measured curve. A follow-up calibration task owns
 * replacing them.
 */
export const PROJECTILE_SPEED_FACTOR_AT_L1 = 1.0; // PROVISIONAL_UNCALIBRATED
export const PROJECTILE_SPEED_FACTOR_AT_L100 = 1.5; // PROVISIONAL_UNCALIBRATED
export const PROJECTILE_RADIUS_FACTOR_AT_L1 = 1.0; // PROVISIONAL_UNCALIBRATED
export const PROJECTILE_RADIUS_FACTOR_AT_L100 = 2.0; // PROVISIONAL_UNCALIBRATED

/**
 * PROVISIONAL safety cap on the still-uncalibrated radius curve: an
 * effective radius may never exceed twice the authored radius.
 *
 * Deliberately kept at 2x rather than raised, even though
 * `PROJECTILE_RADIUS_FACTOR_AT_L100 = 2.0` means a Lv100 (raw 300) build
 * now lands EXACTLY on this boundary — `min(radiusM * radiusFactor(300),
 * radiusM * 2)` is `radiusM * 2` either way the clamp is written, since
 * `radiusFactor(300) === 2` exactly. That coincidence is intentional, not
 * a silent truncation of the curve's intended top: the owner's Lv100
 * target IS 2x, so the clamp and the curve's designed ceiling are the same
 * number by design. The clamp still does real work above raw stat 300
 * (uncalibrated/future high-level allocations), where it caps growth the
 * curve would otherwise continue without bound.
 */
const PROJECTILE_RADIUS_CLAMP_FACTOR = 2;

/** R2: magic-lineage sources drive off MAG, everything else (BODY/WEAPON)
 * off TEC — the same branch `fireBasicAttackProjectile` already uses. */
function precisionStatPoints(primaryStats: PrimaryStats | undefined, sourceRequirement: SourceRequirement | undefined): number {
  return sourceRequirement?.sourceClass === 'MAGIC' ? (primaryStats?.MAG ?? 0) : (primaryStats?.TEC ?? 0);
}

/**
 * Produce this activation's effective `projectile` delivery: authored
 * `speedMps`/`radiusM` scaled by the firing actor's precision stat,
 * relative to the canonical-build anchor (`PROJECTILE_PRECISION_ANCHOR_
 * RAW_STAT_POINTS`), where the anchor build's own factor is exactly 1.0.
 * Source class (WEAPON/MAGIC) selects only WHICH stat drives the factor
 * (`precisionStatPoints`'s TEC-vs-MAG branch) — the factor curve itself
 * (`PROJECTILE_SPEED_FACTOR_AT_L1/L100`, `PROJECTILE_RADIUS_FACTOR_AT_L1/
 * L100`) is the same for every source class.
 *
 * `delivery.type !== 'projectile'` is the identity transform: the same
 * `delivery` reference is returned, untouched.
 *
 * Never called for the basic-attack synthetic skill (`BASIC_ATTACK_SKILL_
 * ID`) — the caller (`executeSkill`, `runtime.ts`) skips this function for
 * that skill id entirely, so basic attacks keep their own pre-existing
 * absolute speed curve (`fireBasicAttackProjectile`'s per-cast
 * `projectileSpeedMultiplier` call, `authoritative-world.ts`) untouched by
 * this relative factor.
 */
export function effectiveProjectileDelivery(
  delivery: Delivery,
  primaryStats: PrimaryStats | undefined,
  sourceRequirement: SourceRequirement | undefined,
): Delivery {
  if (delivery.type !== 'projectile') return delivery;
  // Deliberately NOT the codebase-wide "absent primaryStats == ZERO_PRIMARY_
  // STATS" convention (`state.ts`'s `primaryStats` doc comment) — that
  // convention exists for consumers pricing a character's own weapon/
  // catalyst output, where "no allocation yet" and "allocated nothing" are
  // the same fact. Here, "absent" means something different: a caller that
  // never models a live character's precision stat at all (every burst/
  // generation-time probe player, and plenty of runtime/unit-test
  // scaffolding predating this feature) — not a real character who
  // deliberately holds zero TEC/MAG. Charging the former the same
  // below-ANCHOR accuracy penalty as the latter would let this system's
  // curve silently move burst-legality/appraisal outcomes for skills that
  // never touch a live player, which is exactly the value/precision
  // coupling R5 forbids. Absent primaryStats therefore behaves exactly like
  // the ANCHOR build (factor 1.0) — unaffected.
  if (primaryStats === undefined) return delivery;
  const stat = precisionStatPoints(primaryStats, sourceRequirement);
  const speedFactor = linearByPoints(stat, PROJECTILE_SPEED_FACTOR_AT_L1, PROJECTILE_SPEED_FACTOR_AT_L100)
    / linearByPoints(PROJECTILE_PRECISION_ANCHOR_RAW_STAT_POINTS, PROJECTILE_SPEED_FACTOR_AT_L1, PROJECTILE_SPEED_FACTOR_AT_L100);
  const radiusFactor = linearByPoints(stat, PROJECTILE_RADIUS_FACTOR_AT_L1, PROJECTILE_RADIUS_FACTOR_AT_L100)
    / linearByPoints(PROJECTILE_PRECISION_ANCHOR_RAW_STAT_POINTS, PROJECTILE_RADIUS_FACTOR_AT_L1, PROJECTILE_RADIUS_FACTOR_AT_L100);
  const effectiveRadiusM = Math.min(delivery.radiusM * radiusFactor, delivery.radiusM * PROJECTILE_RADIUS_CLAMP_FACTOR);
  return { ...delivery, speedMps: delivery.speedMps * speedFactor, radiusM: effectiveRadiusM };
}
